import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import StatusDropdown from "../../../../components/MultiSelectDropdown";
import SingleSelectDropdown from "../../../../components/SingleSelectDropdown/SingleSelectDropdown";
import { Form, Row, Col, Button, NavDropdown } from "react-bootstrap";
import MemberLocationDropdown from "../../../../components/form/MemberLocationDropdown";
import MemberPayerDropdown from "../../../../components/form/MemberPayerDropdown";
import { UserContext } from "shared/context/UserContext";
import { useFetchRemittancePayers } from "../hooks/useFetchRemittancePayers";
import { GetObjectPropertyValue } from "shared/helpers/DataHelpers";
import { GetISODate } from "../../../../shared/helpers/DateHelpers";

export default function RemittanceLookupForm(props) {
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedPayers, setSelectedPayers] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [isDateLookup, setDateLookup] = useState(true);
  const [dateType, setDateType] = useState("check");
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date;
  });
  const [toDate, setToDate] = useState(null);
  const [amount, setAmount] = useState("");
  const [isAmountLookup, setAmountLookup] = useState(false);
  const [checkNumber, setCheckNumber] = useState("");
  const [isCheckLookup, setCheckLookup] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const location = useLocation();
  const userContext = useContext(UserContext);

  const { data: remittancePayersData, isLoading: remittancePayersLoading } =
    useFetchRemittancePayers(
      userContext.isAuthenticated,
      userContext.email,
      GetObjectPropertyValue(userContext.currentOrganization, "value"),
      GetObjectPropertyValue(userContext.currentOrganization, "type"),
      location.pathname
    );

  const [customPayerList, setCustomPayerList] = useState(null);

  useEffect(() => {
    if (remittancePayersData && !remittancePayersLoading) {
      const formattedPayers = Object.entries(remittancePayersData)
        .map(([value, label]) => ({
          value: value,
          label: label,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setCustomPayerList(formattedPayers);
    }
  }, [remittancePayersData, remittancePayersLoading]);

  useEffect(() => {
    if (location.state && location.state.linkedCheckNumber) {
      setCheckLookup(true);
      setCheckNumber(location.state.linkedCheckNumber);
      setFromDate(location.state.linkedCheckDate);
      setToDate(location.state.linkedCheckDate);
      setLoaded(true);
      submitForm();
    }
  }, [location.state, selectedLocations]);

  // Auto-submit form on initial load if validation passes
  useEffect(() => {
    if (
      !loaded &&
      !remittancePayersLoading &&
      customPayerList &&
      selectedLocations.length > 0 &&
      selectedPayers.length > 0 &&
      selectedStatus.length > 0
    ) {
      const validationError = validateForm();
      if (!validationError) {
        setLoaded(true);
        submitForm();
      }
    }
  }, [selectedLocations, selectedPayers, selectedStatus, loaded, remittancePayersLoading, customPayerList]);

  // Reset loaded state when user context changes
  useEffect(() => {
    setLoaded(false);
  }, [
    userContext.locations,
    userContext.metricLocations,
    userContext.recLocations,
    userContext.isUnrestrictedEraPath,
  ]);

  const statusOptions = [
    { value: "Archive", label: "Archived" },
    { value: "Pending", label: "Pending" },
  ];

  function validateForm() {
    if (selectedLocations.length === 0)
      return "Location (NCPDP) is a required field";
    if (selectedPayers.length === 0) return "Payer is a required field";
    if (selectedStatus.length === 0) return "Status is a required field";

    if (isDateLookup || isAmountLookup || isCheckLookup) {
      if (isDateLookup) {
        if (!fromDate) return "Start date is a required field";
        // End date is optional, but if provided, it must be after start date
        if (toDate && fromDate > toDate)
          return "Start date cannot be greater than end date";
      }

      function isSpecialCharacters(val, includeDash = false) {
        var pattern = includeDash
          ? new RegExp(/[~`!#$%\^&*+=\[\]\\';,/{}|\\":<>\?]/)
          : new RegExp(/[~`!#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?]/);
        return pattern.test(val);
      }

      if (isAmountLookup) {
        if (amount) {
          if (isSpecialCharacters(amount)) {
            return "Please only use standard alphanumeric characters";
          } else if (isNaN(amount)) {
            return "Amount value should be a number";
          } else {
            let amountVal = amount;
            setAmount(parseFloat(amountVal).toFixed(2));
          }
        } else {
          return "Amount field is required when look up by amount is selected";
        }
      }

      if (isCheckLookup) {
        if (checkNumber) {
          if (isSpecialCharacters(checkNumber, true)) {
            return "Please only use standard alphanumeric characters";
          } else if (checkNumber.length < 3) {
            return "Check Number should be at least 3 characters long";
          }
        } else {
          return "Check field is required when look up by check number is selected";
        }
      }
    } else {
      return "At least one selection should be made for look up by";
    }

    return null;
  }

  function handleFormSubmit(e) {
    e.preventDefault();
    let errors = validateForm();
    errors ? alert(errors) : submitForm();
  }

  function submitForm() {
    props.handleFormSubmit({
      payers: selectedPayers.join(","),
      locations: selectedLocations.join(","),
      status: selectedStatus.join(","),
      dateType: isDateLookup ? dateType : "",
      fromDate: fromDate && isDateLookup ? GetISODate(fromDate) : "",
      toDate: toDate && isDateLookup ? GetISODate(toDate) : "",
      amount: isAmountLookup ? amount : "",
      checkNumber: isCheckLookup ? checkNumber : "",
    });
  }

  function handleExport(exportType) {
    if (props.handlePayerExport) {
      props.handlePayerExport(exportType);
    }
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <Row className="justify-content-start">
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Payer</Form.Label>
          <Form.Control
            as={MemberPayerDropdown}
            onChange={(payers) => setSelectedPayers(payers)}
            customPayerList={customPayerList}
            disabled={remittancePayersLoading || !customPayerList}
          />
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Location (NCPDP)</Form.Label>
          <Form.Control
            as={MemberLocationDropdown}
            onChange={(locations) => setSelectedLocations(locations)}
            programSubscription={"Rec2.0"}
          />
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Remittance Status</Form.Label>
          <StatusDropdown
            options={statusOptions}
            onChange={(Status) => {
              setSelectedStatus(Status);
            }}
            selectAllOption
            filterable
            defaultSelected={[statusOptions[1]]}
            // onLoadAllSelected
          />
        </Col>  
        <Col
          md="auto"
          className="d-flex flex-column"
          style={{ padding: `35px 20px 0px 0px` }}
        >
          <NavDropdown
            title={<span>Payer List Report</span>}
            id="basic-nav-dropdown"
          >
            <NavDropdown.Item onClick={() => handleExport("excel")} href="#">
              Excel
            </NavDropdown.Item>
            <NavDropdown.Item onClick={() => handleExport("csv")} href="#">
              CSV
            </NavDropdown.Item>
          </NavDropdown>
        </Col>
      </Row>
      <Row style={{ marginTop: "15px" }}>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Look Up By...</Form.Label>
          <div key="LookupDate" className="mb-3">
            <Form.Check
              custom={"true"}
              type="checkbox"
              id="lookup1"
              label="Date"
              onChange={(e) => {
                setDateLookup(e.target.checked);
              }}
              defaultChecked
            />
          </div>
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Date Type</Form.Label>
          <SingleSelectDropdown
            options={[
              { value: "check", label: "Check" },
              { value: "remittance", label: "Remit" },
              { value: "archive", label: "Archive" },
              { value: "payment", label: "Payment" },
            ]}
            defaultSelected={{ value: "check", label: "Check" }}
            onChange={(value) => {  
              setDateType(value)}
            }
            disabled={!isDateLookup}
          />
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            name="fromDate"
            as={DatePicker}
            selected={fromDate}
            onChange={(date) => {
              setFromDate(date);
            }}
            disabled={!isDateLookup}
          />
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>End Date</Form.Label>
          <Form.Control
            name="toDate"
            as={DatePicker}
            selected={toDate}
            onChange={(date) => {
              setToDate(date);
            }}
            disabled={!isDateLookup}
          />
        </Col>
        <Col
          md="auto"
          className="d-flex flex-column"
          style={{ padding: `35px 20px 0px 0px` }}
        >
          <div key="LookupAmount" className="mb-3">
            <Form.Check
              custom={"true"}
              type="checkbox"
              id="Lookup2"
              label="Amount"
              checked={isAmountLookup}
              onChange={(e) => {
                setAmountLookup(e.target.checked);
              }}
            />
          </div>
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Amount</Form.Label>
          <Form.Control
            type="text"
            maxLength="19"
            placeholder="Optional"
            onChange={(e) => {
              setAmount(e.target.value);
            }}
            value={amount}
            disabled={!isAmountLookup}
          />
        </Col>
        <Col
          md="auto"
          className="d-flex flex-column"
          style={{ padding: `35px 20px 0px 0px` }}
        >
          <div key="LookupCheckNumber" className="mb-3">
            <Form.Check
              custom={"true"}
              type="checkbox"
              id="lookup3"
              label="Check Number"
              checked={isCheckLookup}
              onChange={(e) => {
                setCheckLookup(e.target.checked);
              }}
            />
          </div>
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Form.Label>Check Number</Form.Label>
          <Form.Control
            type="text"
            maxLength="60"
            placeholder="Optional"
            onChange={(e) => {
              setCheckNumber(e.target.value);
            }}
            value={checkNumber}
            disabled={!isCheckLookup}
          />
        </Col>
        <Col md="auto" className="d-flex flex-column">
          <Button className="inline-show-result-button" size="sm" type="submit">
            Show Results
          </Button>
        </Col>
      </Row>
    </Form>
  );
}
